import React, { useEffect, useState } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import { matchUserProgramToSpecialtyProgram } from '../helpers/helpers'
import {
  IEpiProps,
  IEventRegistrationRefreshModel,
  IUserSelections,
  StepConstants,
} from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { AttendeesReview } from './attendees-review'
import { CardAccordion } from './card-accordion'
import { DateTimeCard } from './date-time-card'
import { ContentWrapper } from './layout/content-wrapper'
import { MainContent } from './layout/main-content'
import { SideBarContent } from './layout/sidebar-content'
import { Modal } from './modal'
import { ProductBasedAddonCard } from './product-based-addon-card'
import { ProgramSummary } from './program-summary'
import { ShareSelections } from './share-selections'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

export const ReviewStep = () => {
  const { currentStep, epiProps, userSelections, eventRegistrationRefreshModel }: IContextProps =
    useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!
  const [showShareSelectionsModal, setShowShareSelectionsModal] = useState(false)
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)

  useEffect(() => {
    const selectedProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
      (p) => p.programCode === userSelections.userProgramSelection?.programCode,
    )

    if (currentStep === StepConstants.ReviewStep) {
      const attendeeCount = userSelections.attendees?.length || 0
      const programPrice = selectedProgram?.programPrice || 0
      const totalProgramPrice = programPrice * attendeeCount
      const totalAddOnPrice = (userSelections.userAddOnProgramSelections || []).reduce((sum, sel) => {
        const addonDef = selectedProgram?.addOnPrograms?.find((a) =>
          sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
        )
        return sum + (addonDef?.programPrice || 0)
      }, 0) * attendeeCount
      const calculatedTotal = totalProgramPrice + totalAddOnPrice
      setTotalPrice(calculatedTotal)
      EcomDataLayerHub.viewReviewSelection(userSelections.userProgramSelection, calculatedTotal)
    }
  }, [currentStep, epiProps.reviewStepHeader, userSelections.userProgramSelection?.programName])

  //Get specialty program details
  const specialtyProgram = matchUserProgramToSpecialtyProgram(
    userSelections.userProgramSelection,
    eventRegistrationRefreshModel
  );

  // Resolve each selected add-on to its catalog definition
  const selectedAddOnPrograms = (userSelections.userAddOnProgramSelections || []).map(sel => ({
    selection: sel,
    definition: specialtyProgram?.addOnPrograms?.find((a) =>
      sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
    )
  })).filter(({ definition }) => definition != null) as { selection: typeof userSelections.userAddOnProgramSelections[0], definition: NonNullable<typeof specialtyProgram>['addOnPrograms'][0] }[]

  const handleContinue = async () => {
    setIsSubmitting(true);
    setShowValidationMessage(false);
    setValidationMessage('');

    try {
      // Format data as AddToCartModel
      const events = [];

      // Add main program event
      if (userSelections.userProgramSelection && userSelections.userProgramDateTimeSelection) {
        events.push({
          CustomerId: null,
          Sku: userSelections.userProgramDateTimeSelection.seatSku,
          EventNumber: userSelections.userProgramDateTimeSelection.eventNumber,
          Participants: userSelections.attendees.length.toString(),
          ProgramType: userSelections.userProgramSelection.isRenewal
            ? 'Renewal'
            : userSelections.userProgramSelection.isDirectDelivery
              ? 'DirectDelivery'
              : 'ICP',
          Specialty: userSelections.userProgramSelection.programCode
        });
      }

      // Add an event for each selected add-on
      for (const { selection, definition } of selectedAddOnPrograms) {
        const programType = userSelections.userProgramSelection.isRenewal
          ? 'Renewal'
          : userSelections.userProgramSelection.isDirectDelivery
            ? 'DirectDelivery'
            : 'ICP'

        if (!selection.disableDateTimeSelection && userSelections.userAddOnProgramDateTimeSelection) {
          // Event-based add-on: use the date/time selection's seat SKU and event number
          events.push({
            CustomerId: null,
            Sku: userSelections.userAddOnProgramDateTimeSelection.seatSku,
            EventNumber: userSelections.userAddOnProgramDateTimeSelection.eventNumber,
            Participants: userSelections.attendees.length.toString(),
            ProgramType: programType,
            Specialty: selection.programCode
          })
        } else if (definition.programSku) {
          // Product-based add-on: no event number
          events.push({
            CustomerId: null,
            Sku: definition.programSku,
            EventNumber: null,
            Participants: userSelections.attendees.length.toString(),
            ProgramType: programType,
            Specialty: selection.programCode
          })
        }
      }

      // Format attendees
      const attendees = userSelections.attendees.map((attendee) => ({
        FirstName: attendee.firstName,
        LastName: attendee.lastName,
        Email: attendee.email,
        Title: attendee.title,
        AttendeeId: null,
        CustomerId: null
      }));

      const addToCartModel = {
        Events: events,
        Attendees: attendees
      };

      const queryString = `attendees=${encodeURIComponent(JSON.stringify(addToCartModel))}`;
      const response = await fetch(`/internalapi/EventRegistrationRefreshApi/AddToCartRefresh?${queryString}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`)
      }

      if (response.ok) {
        const result = await response.json()
        // Check for ineligible attendees
        if (result.ineligibleAttendees && result.ineligibleAttendees.length > 0) {
          setValidationMessage(
            `${epiProps.addToCartErrorMessageSomeAttendeesIneligible}: ${result.ineligibleAttendees.map((a: any) => a.email).join(', ')}`,
          )
          setShowValidationMessage(true);
        } else {
          // Success - redirect to cart
          EcomDataLayerHub.confirmEnrollment(userSelections, totalPrice)
          window.location.href = result.message;
        }
      } else {
        setValidationMessage(epiProps.addToCartErrorMessage);
        setShowValidationMessage(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setValidationMessage(epiProps.addToCartErrorMessage);
      setShowValidationMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (currentStep == StepConstants.ReviewStep) {
    return (
      <div>
        <section className="app-section-header">
          <div className="app-section-header-inner">
            <h1>{epiProps.reviewStepHeader}</h1>
          </div>
        </section>
        <ContentWrapper>
          <MainContent>
            <AttendeesReview showEditCta={true} />
            <div className="card">
              {userSelections.userProgramSelection && (
                <div className="card-section">
                  <h3 className="card-header-title">{userSelections.userProgramSelection.programName}</h3>

                  {userSelections.userProgramDateTimeSelection && (
                    <DateTimeCard
                      dateTimeEvent={userSelections.userProgramDateTimeSelection}
                      isSelected={true}
                      onSelect={() => { }} // No selection needed in review
                      isReviewStep={true}
                      isAddOn={false}
                    />
                  )}
                  {
                  specialtyProgram &&
                    <CardAccordion title={epiProps.programDetailsHeaderText} includeSnippet>
                      <div
                        className="rich-text"
                        dangerouslySetInnerHTML={{ __html: specialtyProgram.programDetails }}
                      />
                      <div className="card-split-layout">
                        <div
                          className="rich-text"
                          dangerouslySetInnerHTML={{ __html: specialtyProgram.programDetailsLeft }}
                        />
                        <div
                          className="rich-text"
                          dangerouslySetInnerHTML={{ __html: specialtyProgram.programDetailsRight }}
                        />
                      </div>
                    </CardAccordion>
                  }
                </div>
              )}

              {selectedAddOnPrograms.map(({ selection, definition }) => (
                <div key={selection.programSku || selection.programCode} className="card-section">
                  <h2 className="card-header-title">{selection.programName}</h2>

                  {!selection.disableDateTimeSelection && userSelections.userAddOnProgramDateTimeSelection ? (
                    <DateTimeCard
                      dateTimeEvent={userSelections.userAddOnProgramDateTimeSelection}
                      isSelected={true}
                      onSelect={() => { }}
                      isReviewStep={true}
                      isAddOn={true}
                    />
                  ) : selection.disableDateTimeSelection ? (
                    <ProductBasedAddonCard
                      program={definition}
                      showPrice={eventRegistrationRefreshModel.showPrice}
                      currency={eventRegistrationRefreshModel.currency}
                      isReviewMode={true}
                    />
                  ) : null}

                  {definition.detailsDescription && (
                    <div className="card-addon-details">
                      <CardAccordion title={epiProps.addOnDetailsHeaderText} includeSnippet>
                        <div className="rich-text" dangerouslySetInnerHTML={{ __html: definition.detailsDescription }} />
                      </CardAccordion>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </MainContent>
          <SideBarContent>
            <ProgramSummary
              userSelections={userSelections}
              onContinue={handleContinue}
              showValidationMessage={showValidationMessage}
              validationMessage={validationMessage}
              showShareSelectionsModalButton={true}
              onShowShareSelectionsModal={() => setShowShareSelectionsModal(true)}
              isSubmitting={isSubmitting}
              hideContinueButton={eventRegistrationRefreshModel.showPrice === false} //Add to cart cta shows only when pricing is displayed (US/CA only)
              showWarningMessage={eventRegistrationRefreshModel.showPrice === false} //Call message will show instead of add to cart CTA.
              warningMessage={epiProps.callMessageReviewStep}
            />
          </SideBarContent>
        </ContentWrapper>

        <Modal
          isOpen={showShareSelectionsModal}
          onClose={() => setShowShareSelectionsModal(false)}
          title={epiProps.shareSelectionsModalHeading}
          size="fullscreen"
          additionalClassName="share-modal"
        >
          <ShareSelections />
        </Modal>
      </div>
    )
  }
}
